import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditIcon from '@mui/icons-material/Edit';
import { Service } from '../../../models/masters/service';
import { SelectServicesDialog } from '../../services/select-services.dialog';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { servicesColumns } from '../../services/grid-columns';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { FormGridEditable } from '../../../components/hook-form/form-grid-editable.component';
import { servicesEditableColumns } from '../../services/grid-editable-columns';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { GridCellProps, GridCustomCellProps } from '@progress/kendo-react-grid';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import ConfirmDialog from '../../../components/confirm-dialog';
import ServiceNotReadyFields from '../../services/service-not-ready-fields';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';

const SELECTED_FIELD = 'isSelected';

interface CellProps {
  projectService: any;
}

interface CustomGridCellProps {
  field: string;
}
interface ServicesFormProps {
  name: string;
  page?: string;
  id?: number;
  parentLoading?: boolean;
  pageAction: string;
  refetchProjectDetails?: () => void;
  project?: any;
}

const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];

export const ServicesForm = React.memo(({ name, id, parentLoading, refetchProjectDetails, project, pageAction, page }: ServicesFormProps) => {

  const isActivePermission = useAuthorize([UserPermissionName?.SET_ACTIVE_INACTIVE]);

  const { setValue, getValues, watch } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [currentServiceId, setCurrentServiceId] = useState(0);
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const [checkedServiceDialog, setCheckedServiceDialog] = useState<boolean>(false);
  const [checkedIsActiveDialog, setCheckedIsActiveDialog] = useState<boolean>(false);
  const [checkedDialogAction, setCheckedDialogAction] = useState<any>();
  const [checkedService, setCheckedService] = useState<any>();
  const [updatedServiceswhenWorkOderCreate, setUupdatedServiceswhenWorkOderCreate] = useState<Service[]>([]);
  const [updatedService, setUpdatedService] = useState<Service[]>([]);
  const isDisableEditableColumn = useAuthorize([UserPermissionName.EDIT_SREVICE_PRICE_PROJECT]);
  const isDisableCheckBoxBasedOnPermission = useAuthorize([UserPermissionName.DELETE_SERVICE_PROJECT]);

  const [projectServiceData, setProjectServiceData] = useState<any[]>();

  const { data, isLoading } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceOfficeMappings(getValues('office.officeID'))
  );

  const { isLoading: isLoadingGenerateWorkOrder, mutateAsync } = useSaveData<any>(
    workOrdersApiKeyStore.generateNewWorkOrder()
  );

  const { isLoading: isIsActiveServiceLoading, mutateAsync: mutateIsActiveServiceAsync } = useSaveData<any>(
    projectsApiKeyStore.isActiveServiceProject());

  useWatch({
    name: ['office.officeID'] || [],
  });

  const [initialService, setInitialService] = useState<Service[]>(
    (watch(name) || []).map((service: any) => ({
      ...service,
      isSelected: false
    }))
  );

  const handleServiceWithPercentage = (serviceData: any) => {
    let updatedServiceData = [];
    if (serviceData?.length > 0) {
      updatedServiceData = serviceData?.map((item: any) => ({
        ...item,
        isSelected: false
      }));
    }
    // Always filter out isNotReadyService
    const filtered = updatedServiceData.filter((item: any) => !item.isNotReadyService);
    setValue(name, updatedServiceData);
    setFilteredService(filtered);

  }

  useEffect(() => {
    if (data && getValues('office.officeID')) {
      if (!id) {
        handleServiceWithPercentage(data.data);
        setUpdatedService(data.data);
      }
    } else {
      if (!id) {
        handleServiceWithPercentage([]);
        setUpdatedService([]);
      }
    }
  }, [data, getValues('office.officeID')]);

  useEffect(() => {
    // Listen for changes in the form state after calling setValue
    if (id && !!project) {
      handleServiceWithPercentage(project?.projectServices);
      setUupdatedServiceswhenWorkOderCreate(project?.projectServices);
    }
  }, [project]);

  useEffect(() => {
    if (updatedServiceswhenWorkOderCreate?.length > 0 && !!project) {
      handleServiceWithPercentage(updatedServiceswhenWorkOderCreate);
    }
  }, [updatedServiceswhenWorkOderCreate, !!project]);

  useEffect(() => {
    // Listen for changes in the form state after calling setValue
    if (!id) {
      handleServiceWithPercentage(updatedService);
    }
  }, [updatedService]);

  const customColumns: GridColumnConfig[] = [
    {
      title: 'Sort Order',
      field: 'sortOrder',
      filter: 'numeric',
      width: '110',
    },
    {
      title: 'Service Name',
      field: 'serviceName',
      filter: 'text',
      searchable: true,
      width: '180',
    },
    {
      title: 'Predecessor',
      field: 'predecessorNames',
      filter: 'text',
      width: '140',
    },
    {
      title: 'Billing Type',
      field: 'billingType',
      filter: 'text',
      width: '118',
    },
    {
      title: 'Service Group',
      field: 'serviceGroupName',
      filter: 'text',
      width: '130',
    },
    {
      title: 'Invoice Amount ($)', // Main heading
      field: '1', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      className: "invoice-amount-class",
      children: [
        {
          title: 'Service',
          field: 'price',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
          // width: '100',
        },
        {
          title: 'Retest',
          field: 'invoiceRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
          // width: '100',
        },
      ],
    },
    {
      title: 'Accrual Amount ($)', // Main heading for accrual
      field: '2', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      className: "accrual-amount-class",
      children: [
        {
          title: 'Service',
          field: 'accrualAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
          // width: '100',
        },
        {
          title: 'Retest',
          field: 'accrualRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
          // width: '100',
        },
      ],
    },
    {
      title: 'Match Billing',
      field: 'matchbilling',
      filter: 'text',
      width: '150',
    },
    {
      title: 'Percentage',
      field: 'percentage',
      filter: 'text',
      fieldType: 'currency',
      editable: true,
      editor: 'numeric',
      isDecimal: true,
      isDisableByTime: true,
      // isDisable: !isDisableEditableColumn,
      // width: '100',
    },
    {
      title: 'Is Active',
      field: 'isActive',
      filter: 'text',
      hide: false,
      width: '100',
    },
    {
      title: 'Create WO',
      field: 'createWO',
      filter: 'text',
      hide: false,
      width: '130',
    },
    // {
    //   title: 'Option',
    //   field: 'option',
    //   filter: 'text',
    //   width: '195',
    // },
  ];

  const servicesEditableColumnsWithPermission: GridColumnConfig[] = customColumns;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleOpenSelectServiceDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseSelectServiceDialog = (services: Service[]) => {
    const mergedData = services?.map(item1 => {
      const matchingItem = projectServiceData?.find(item2 => item2?.serviceID === item1?.serviceID);
      const matchingPriceItem = getValues(name)?.find((item2: any) => item2?.serviceID === item1?.serviceID);
      return {
        ...item1,
        isActive: matchingItem ? matchingItem?.isActive : true,
        price: matchingPriceItem?.price ? matchingPriceItem?.price : item1?.price,
        invoiceRetestAmount: matchingPriceItem?.invoiceRetestAmount ? matchingPriceItem?.invoiceRetestAmount : item1?.invoiceRetestAmount,
        accrualAmount: matchingPriceItem?.accrualAmount ? matchingPriceItem?.accrualAmount : item1?.accrualAmount,
        accrualRetestAmount: matchingPriceItem?.accrualRetestAmount ? matchingPriceItem?.accrualRetestAmount : item1?.accrualRetestAmount
      };
    });
    setOpenDialog(false);
    handleServiceWithPercentage(mergedData);
  };

  const handleGenerateWork = async (serviceID: any) => {
    setCurrentServiceId(serviceID);
    setIsConfirmation(true);
  };

  const handleClose = () => {
    setIsConfirmation(false);
  };

  const handleConfirm = async () => {
    await mutateAsync({
      data: {
        projectID: id,
        serviceID: currentServiceId,
      },
    }).then((data) => {
      refetchProjectDetails?.();
      handleClose();
    });
  };

  const conditionalCellRender = (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (props.field === SELECTED_FIELD && props.dataItem?.isBundled) {
      return (
        <Tooltip title={<Typography variant="body1">Already used in bundle creation</Typography>}>
          <td>
            <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.isBundled} />
          </td>
        </Tooltip>
      );
    } else if (props.field === SELECTED_FIELD && props.dataItem?.scheduledWorkOrderExists) {
      return (
        <Tooltip
          title={
            <Typography variant="body1">
              Deletion is not possible as there is a scheduled work order in the Lots/Units for this service.
            </Typography>
          }
        >
          <td>
            <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.scheduledWorkOrderExists} />
          </td>
        </Tooltip>
      );
    } else if (props.field === SELECTED_FIELD && props.dataItem?.isRetestService) {
      return (
        <td>
          <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.isRetestService} />
        </td>
      );
    } else if (
      props.field === SELECTED_FIELD &&
      props.dataItem?.mappingID !== 0 &&
      !isDisableCheckBoxBasedOnPermission
    ) {
      return (
        <Tooltip title={<Typography variant="body1">You do not have permission for delete</Typography>}>
          <td>
            <Checkbox checked={props.dataItem?.isSelected} disabled={true} />
          </td>
        </Tooltip>
      );
    } else {
      return defaultRendering;
    }
  };

  const EditCell = React.memo(({ projectService }: CellProps) => {
    const [hovered, setHovered] = useState(false);
    const handleMouseOver = () => {
      setHovered(true);
    };
    const handleMouseOut = () => {
      setHovered(false);
    };

    const buttonStyle = {
      color: 'white',
      backgroundColor: hovered ? '#0000b5' : '#0000b5',
      padding: '8px',
      maxHeight: '28px',
    };

    return (
      <td className="stickyCloumn">
        <div className="actionWidth">
          {projectService?.dataItem?.lotwithoutWorkOrderExists && projectService?.dataItem?.mappingID !== 0 && (
            <Button
              onClick={() => handleGenerateWork(projectService?.dataItem?.serviceID)}
              className="btn  btn-primary btn-sm sitDefaultBtn"
              style={buttonStyle}
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}

            // onClick={() => openDialog(s.workOrderStatus, s.showNewResultPopup)}
            >
              Create Work Order
            </Button>
          )}
        </div>
      </td>
    );
  });

  const handleCheckboxClicked = (checkedValue: boolean, service: any) => {
    setCheckedService(checkedValue ? service : undefined);
    setCheckedServiceDialog(checkedValue);
  }

  const MatchBillingCell = React.memo(({ projectService }: CellProps) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // setChecked(event.target.checked);
      handleCheckboxClicked(event?.target?.checked, projectService?.dataItem)
    };

    return (
      <td>
        {projectService?.dataItem?.invoiceRetestAmount && (
          <Checkbox
            checked={projectService?.dataItem?.invoiceRetestAmount == projectService?.dataItem?.accrualRetestAmount}
            onChange={handleChange}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        )}
      </td>
    );
  });

  const handleisActiveCheckboxClicked = (checkboxVal: any, service: any) => {
    setCheckedService(service);
    setCheckedIsActiveDialog(true);
    setCheckedDialogAction({
      value: checkboxVal, message: checkboxVal ?
        "Do you want to activate all unscheduled, inactive work orders across all lots in this project?" :
        "Do you want to inactivate all unscheduled work orders across all lots in this project?"
    })
  }

  const ServiceIsActiveCell = React.memo(({ projectService }: CellProps) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleisActiveCheckboxClicked(event?.target?.checked, projectService?.dataItem)
    };

    return (
      <td>
        {isActivePermission && projectService?.dataItem?.mappingID !== 0 && (
          <Checkbox
            checked={projectService?.dataItem?.isActive}
            onChange={handleChange}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        )}
      </td>
    );
  });

  const handleCreateWOAction = (checkboxVal: any, signleService: any) => {
    const updatedServices = getValues(name)?.map((service: any) => {
      if (service.serviceID === signleService?.serviceID) {
        return {
          ...service,
          createWO: checkboxVal
        };
      }
      return service;
    });

    setValue(name, updatedService);
    setFilteredService(updatedServices);
  }

  const ServiceIsCreateWOCell = React.memo(({ projectService }: CellProps) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleCreateWOAction(event?.target?.checked, projectService?.dataItem)
    };

    return (
      <td>
        <Checkbox
          checked={projectService?.dataItem?.createWO}
          onChange={handleChange}
          inputProps={{ 'aria-label': 'controlled' }}
          disabled={!projectService?.dataItem?.isActive}
        />
      </td>
    );
  });

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'option') {
      return (dataItem: any) => {
        return <EditCell projectService={dataItem} />;
      };
    }
    if (field === 'matchbilling') {
      return (dataItem: any) => {
        return <MatchBillingCell projectService={dataItem} />;
      };
    }
    if (field === 'isActive') {
      return (dataItem: any) => {
        return <ServiceIsActiveCell projectService={dataItem} />;
      };
    }
    if (field === 'createWO') {
      return (dataItem: any) => {
        return <ServiceIsCreateWOCell projectService={dataItem} />;
      };
    }
    // Return undefined if the field is not 'bluePrint'
    return undefined;
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout(`${pageAction}ProjectService`, `${pageAction}ProjectService`));

  const { mutateAsync: saveGridLayout, isLoading: isSaveGridLoading } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredService, setFilteredService] = useState<Service[]>(watch(name));

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const services = watch(name);

  useEffect(() => {
    const filteredServiceList = initialService
      ?.filter((d: Service) => !d.isNotReadyService)
      .filter((service: Service) =>
        Object.values(service).some(
          (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
      );
    setFilteredService(filteredServiceList || []);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const serviceList = filteredService
      ?.filter((d: Service) => !d.isNotReadyService);
    setProjectServiceData(serviceList || []);
  }, [filteredService])

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleResetService = () => {
    const updatedService: any = filteredService?.map(service => {
      const { percentage, ...rest } = service;
      return { ...rest };
    });
    setFilteredService(updatedService ?? []);
    selectRefs.current = {};
  }

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: `${pageAction}ProjectService`,
      pageName: `${pageAction}ProjectService`,
    };

    saveGridLayout({ data }).then(() => {
      refetchGridLayout();
    });
    handleResetService();
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: `${pageAction}ProjectService`,
      pageName: `${pageAction}ProjectService`,
    };

    saveGridLayout({ data }).then(() => {
      refetchGridLayout();
    });
    handleResetService();
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  const areArraysEqual = (arr1: Service[], arr2: Service[]) => {
    return arr1.every((item, index) => item?.serviceID === arr2[index]?.serviceID);
  };

  useEffect(() => {
    if (!services || services?.length === 0) {
      return;
    }
    if (!areArraysEqual(services, filteredService)) {
      setFilteredService(services?.filter((d: Service) => !d.isNotReadyService));
    }
  }, [services]);

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const internalValueRefs = useRef<Record<string, string>>({});
  const selectRefs = useRef<Record<string, string | null>>({});
  const { showSnackbar } = useSnackbar();

  const formatToTwoDecimals = (value: any) => {
    // Convert to number in case it's a string
    const num = Number(value);

    // Check if it's an integer (no decimals)
    if (Number.isInteger(num)) {
      return num.toFixed(2); // Add ".00"
    }

    return value.toString(); // Already has decimals, return as-is
  };


  const CustomGroupHeaderCell = (props: any) => {
    const [hovered, setHovered] = useState(false);
    const handleMouseOver = () => {
      setHovered(true);
    };
    const handleMouseOut = () => {
      setHovered(false);
    };

    const buttonStyle = {
      color: 'white',
      backgroundColor: hovered ? '#0000b5' : '#0000b5',
      padding: '8px',
      maxHeight: '28px',
      marginTop: '6px',
    };
    const groupKey = props?.dataItem?.value;
    const [inputValues, setInputValues] = useState<Record<string, string>>({});

    const [selectedModes, setSelectedModes] = useState<Record<string, '' | 'Equal' | 'Percentage'>>({});
    const inputValue = inputValues[groupKey] ?? internalValueRefs.current[groupKey] ?? '';

    const selectedMode = selectedModes[groupKey] ?? selectRefs.current[groupKey] ?? '';

    const totalAccrualAmount = props?.dataItem?.items?.reduce((total: any, service: any) => {
      const amount = parseFloat(service.accrualAmount);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);

    const totalPriceAmount = props?.dataItem?.items?.reduce((total: any, service: any) => {
      const amount = parseFloat(service.price);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);

    const calculateAmountPerService = (amount: number, service: Service, mode: string, count: number): number =>
      isNaN(amount) || count <= 0
        ? 0
        : mode === 'Equal'
          ? amount / count
          : mode === 'Percentage'
            ? (amount * service?.percentage) / 100
            : 0;

    const handleClick = () => {
      const amount = parseFloat(inputRefs.current[groupKey]?.value ?? '');
      if (isNaN(amount)) {
        showSnackbar('Enter a Contract Value', 'error');
        return;
      }

      if (!selectedMode) {
        showSnackbar('Please Select Distribution Type', 'error');
        return;
      }

      const selectedServices = props.dataItem?.items?.filter((data: any) => !!data?.isSelected);

      if (!selectedServices?.length) {
        showSnackbar('No selected services in this group.', 'error');
        return;
      }

      if (selectedMode === 'Percentage') {
        let totalPercentage = 0;
        for (const service of selectedServices) {
          const percentage = Number(service.percentage);
          if (isNaN(percentage) || percentage <= 0) {
            showSnackbar('All selected services must have a valid percentage entered.', 'error');
            return;
          }
          totalPercentage += percentage;
        }

        if (totalPercentage > 100) {
          showSnackbar('Total percentage cannot exceed 100%.', 'error');
          return;
        }
      }

      const updatedSelectedServices = selectedServices.map((service: Service) => ({
        ...service,
        accrualAmount: (calculateAmountPerService(amount, service, selectedMode, selectedServices?.length)).toString(),
        accrualRetestAmount: (calculateAmountPerService(amount, service, selectedMode, selectedServices?.length)).toString(),
      }));

      const updatedFiltered = filteredService.map((originalService: any) => {
        let updatedService = {
          ...originalService,
          isSelected: false,
        };

        const match = updatedSelectedServices.find((s: any) => s.serviceID === originalService.serviceID);
        if (match) {
          updatedService = match;
        }

        return updatedService;
      });

      internalValueRefs.current[groupKey] = "";
      setInputValues((prev) => ({
        ...prev,
        [groupKey]: "",
      }));

      selectRefs.current[groupKey] = "";
      setSelectedModes((prev) => ({
        ...prev,
        [groupKey]: "",
      }));

      const updatedService = updatedFiltered?.map(service => {
        const matched = props?.dataItem?.items?.find((item: any) => item.serviceID === service.serviceID);

        if (matched) {
          const { percentage, ...rest } = service;
          return { ...rest, isSelected: false };
        }

        return service;
      });

      setValue(name, updatedService)
      setFilteredService(updatedService);
    };

    const handleContractValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      internalValueRefs.current[groupKey] = value;

      setInputValues((prev) => ({
        ...prev,
        [groupKey]: value,
      }));
    };

    const handleSelectInputChange = (e: any) => {
      selectRefs.current[groupKey] = e.target.value;

      const updatedData: any = filteredService?.map(service => {
        const matched = props?.dataItem?.items?.find((item: any) => item.serviceID === service.serviceID);

        if (e.target.value === 'Percentage') {
          // If matched, add percentage
          if (matched) {
            return { ...service, percentage: "" };
          } else {
            return service;
          }
        } else {
          // In all cases, remove percentage if it exists
          const { percentage, ...rest } = service;
          return rest;
        }
      });
      setFilteredService(updatedData);

      setSelectedModes((prev) => ({
        ...prev,
        [groupKey]: e.target.value as 'Equal' | 'Percentage',
      }));
    };
    const invoiceAmountElement: any = document?.querySelector('.invoice-amount-class');
    const invoiceAmountLeft = invoiceAmountElement?.getBoundingClientRect()?.left;

    const accrualAmountelement: any = document?.querySelector('.accrual-amount-class');
    const accrualAmountLeft = accrualAmountelement?.getBoundingClientRect()?.left;


    if (props?.dataItem?.field === 'serviceGroupName') {
      return (
        props.tdProps && (
          <td
            {...props.tdProps}
            style={{
              backgroundColor: props.color,
              position: 'relative'
            }}
          >
            {props.children && (
              <>
                <p className="k-reset">
                  {props.children.props.children[0]}
                  {props.dataItem[props.field]}
                  <div style={{ display: 'flex', minWidth: 455, gap: 6, paddingLeft: 10 }}>
                    <FormControl size="small" style={{ width: 170 }}>
                      <TextField
                        type="number"
                        value={inputValue}
                        inputRef={(el) => {
                          if (el) inputRefs.current[groupKey] = el;
                        }}
                        onChange={handleContractValueInputChange}
                        style={{ width: 170 }}
                        size="small"
                        label="Contract Value"
                      />
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel id={`distributionType-select-label`}>Distribution Type</InputLabel>
                      <Select
                        value={selectedMode}
                        onChange={handleSelectInputChange}
                        variant="outlined"
                        size="small"
                        placeholder="Select"
                        style={{ width: 190 }}
                        label="Distribution Type"
                      >
                        <MenuItem value="Equal">Equal</MenuItem>
                        <MenuItem value="Percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      className="btn  btn-primary btn-sm sitDefaultBtn"
                      style={buttonStyle}
                      onMouseOver={handleMouseOver}
                      onMouseOut={handleMouseOut}
                      onClick={handleClick}
                    >
                      Apply
                    </Button>
                  </div>
                </p>
                <div style={{ position: 'absolute', left: `calc(${invoiceAmountLeft}px - 113px)`, top: '16px', pointerEvents: 'none' }}>
                  <Typography variant='body1'><b style={{ paddingLeft: 5 }}>${formatToTwoDecimals(totalPriceAmount?.toFixed(2))}</b></Typography>
                </div>
                <div style={{ position: 'absolute', left: `calc(${accrualAmountLeft}px - 107px)`, top: '16px' }}>
                  <Typography variant='body1'><b style={{ paddingLeft: 5 }}>${formatToTwoDecimals(totalAccrualAmount?.toFixed(2))}</b></Typography>
                </div>
              </>
            )}
          </td>
        )
      );
    }

    return (
      props.tdProps && (
        <td
          {...props.tdProps}
          style={{
            backgroundColor: props.color,
          }}
        >
          {props.children && (
            <p className="k-reset">
              {props.children.props.children[0]}
              {props.dataItem[props.field]}
            </p>
          )}
        </td>
      )
    );
  };

  const handleClosedCheckoxDialog = () => {
    setCheckedService(undefined);
    setCheckedServiceDialog(false);
  }

  const handleSumbitConfirm = () => {
    const updatedServices = getValues(name)?.map((service: any) => {
      if (service.serviceID === checkedService?.serviceID) {
        return {
          ...service,
          accrualRetestAmount: service.invoiceRetestAmount || ""
        };
      }
      return service;
    });
    handleServiceWithPercentage(updatedServices);
    setFilteredService(updatedServices);
    handleClosedCheckoxDialog();
  }

  const handleClosedIsActiveCheckoxDialog = () => {
    setCheckedService(undefined);
    setCheckedIsActiveDialog(false);
    setCheckedDialogAction(undefined);
  }

  const handleSumbitIsActiveConfirm = async (action: string) => {
    const updatedServices = getValues(name)?.map((service: any) => {
      if (service.serviceID === checkedService?.serviceID) {
        return {
          ...service,
          isActive: checkedDialogAction?.value,
          createWO: false,
          lotwithoutWorkOrderExists: false
        };
      }
      return service;
    });
    const isActivePayload = {
      serviceID: checkedService?.serviceID,
      projectID: id,
      confirmationType: action,
      isActive: checkedDialogAction?.value
    }
    await mutateIsActiveServiceAsync({ data: isActivePayload })
    setValue(name, updatedService);
    setFilteredService(updatedServices);
    handleClosedIsActiveCheckoxDialog();
  }

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <Card elevation={0}>
        <ServiceNotReadyFields />
        <CardHeader
          className="subHead"
          title={
            <Typography variant="h3" color="textPrimary">
              PROJECT SERVICES
            </Typography>
          }
          action={
            <>
              <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE_PROJECT]}>
                <Button
                  className="btnLink"
                  variant="text"
                  sx={{ p: 0, m: 0, mr: 1 }}
                  onClick={handleOpenSelectServiceDialog}
                >
                  Modify Service
                </Button>
                <IconButton onClick={handleExpandClick}>
                  {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </IconButton>
              </GuardedComponent>
            </>
          }
        />
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent className="formContainer">
            <Grid container>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormGridEditable
                  fileTitle='project_service'
                  dataItemKey="serviceID"
                  columns={servicesEditableColumnsWithPermission?.map((col) =>
                    col.field === 'isActive' || col.field === 'createWO' ?
                      { ...col, hide: pageAction !== 'edit' ? true : false } :
                      col
                  )}
                  name={name}
                  isLoading={isLoading}
                  customGridCell={CustomGridCell}
                  dataItems={projectServiceData || []}
                  reorderable
                  handleSaveLayout={handleSaveLayout}
                  isSaveLayout={true}
                  gridConfigFromApi={gridConfig}
                  handleResetLayout={handleResetLayout}
                  customGroupHeaderCell={(page == 'Project' && (pageAction == 'edit' || pageAction == 'create')) ? CustomGroupHeaderCell : undefined}
                  // customGroupFooterCell={customGroupFooterCell}
                  selectedField={'isSelected'}
                  isBusy={isLoadingGridLayout || isSaveGridLoading || parentLoading}
                />
              </Grid>
            </Grid>
            {isConfirmation && (
              <ConfirmDialog
                message="Are you sure that you want to generate work order?"
                onClose={handleClose}
                onConfirm={handleConfirm}
                open={isConfirmation}
                isLoading={isLoadingGenerateWorkOrder}
              />
            )}
            {openDialog && (
              <SelectServicesDialog
                open={openDialog}
                isNotReadyService
                handleCancelClick={() => setOpenDialog(false)}
                onClose={handleCloseSelectServiceDialog}
                services={getValues(name) ?? []}
                conditionalCellRender={conditionalCellRender}
                preventCheckAndUnCheckOnHeaderelectionChange={['isBundled', 'isRetestService']}
                isShowUncheckConfirmation={true}
                selectedField="isSelected"
                // selectedField={isDisableCheckBoxBasedOnPermission ? 'isSelected' : undefined}
                // servicesApiQuery={() => servicesApiKeyStore.getServiceOfficeMappings(getValues('office.officeID'))}
                servicesApiQuery={() =>
                  servicesApiKeyStore.getServiceMappingList(0, !id ? getValues('office.officeID') : 0, !id ? 0 : id, 0, 0)
                }
              />
            )}
          </CardContent>
        </Collapse>
      </Card>
      {checkedServiceDialog && (
        <Dialog
          open={checkedServiceDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px', textAlign: 'center' }}>
              <div>Do you want the Accrual Reinspection Price to match the Billing Reinspection Price?</div>
              {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                disabled={isReadOnlyPermission}
                onClick={handleClosedCheckoxDialog}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={handleSumbitConfirm}
                disabled={isLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {checkedIsActiveDialog && (
        <Dialog
          open={checkedIsActiveDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px', textAlign: 'center' }}>
              <div>{checkedDialogAction?.message ?? ''}</div>
              {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
            </div>
          </div>
          <DialogActionsBar>
            {/* {isReadOnlyPermission && ( */}
            <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={handleClosedIsActiveCheckoxDialog}>
              Cancel
            </Button>
            {/* )} */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: (isIsActiveServiceLoading || isReadOnlyPermission) ? '#d7dbdd' : '#0000b5',
                  color: (isIsActiveServiceLoading || isReadOnlyPermission) ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                disabled={isIsActiveServiceLoading || isReadOnlyPermission}
                onClick={() => handleSumbitIsActiveConfirm('no')}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: (isIsActiveServiceLoading || isReadOnlyPermission) ? '#d7dbdd' : '#0000b5',
                  color: (isIsActiveServiceLoading || isReadOnlyPermission) ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={() => handleSumbitIsActiveConfirm('yes')}
                disabled={isIsActiveServiceLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
});
